/**
 * CaseSendEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides collection-case-specific
 * props (source type, linked transaction IDs for invoice attachment fetching).
 *
 * Follows the exact same pattern as TransactionSendCopyInvoiceModal.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import { SingleEmailModal } from '@/components/shared/single-email-modal';
import { useAdmDocumentDownloadAsBase64 } from '@/hooks/useAdmDocumentDownloadAsBase64';
import { useApi } from '@/hooks/useApi';
import { useCaseTransactionIds } from '@/hooks/useCaseTransactionLinks';
import { useCompanyProductType } from '@/hooks/useCompanyProductType';
import type { PaymentLinkBelowMinimum } from '@/hooks/useEmailMergeData';
import { type StageSkipConfirmation, useLogCaseEmail } from '@/hooks/useLogCaseEmail';
import { useTransactionDocumentDownload } from '@/hooks/useTransactionDocumentDownload';
import { fetchAdmDocumentsForTransaction } from '@/hooks/useTransactionDocuments';
import {
  attachmentErrorMessages,
  attachmentsError,
  emptyAttachmentsResult,
  type FetchAttachmentsResult,
  fetchAttachmentsWithProgress,
} from '@/lib/fetch-attachments-utils';
import { trackError } from '@/lib/telemetry';
import { useAuth } from '@/providers/AuthProvider';
import { useDocumentApi } from '@/providers/DocumentApiProvider';
import { useMode } from '@/providers/ModeProvider';
import type { TransactionApi } from '@/types/api/transactions';

export interface CaseSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  customerId: string;
  customerName?: string;
  accountNumber?: string;
  onSuccess?: () => void;
  /** Called when the backend indicates the next automated strategy stage needs a skip confirmation */
  onStageConfirmationRequired?: (confirmation: StageSkipConfirmation) => void;
}

export function CaseSendEmailModal({
  open,
  onOpenChange,
  caseId,
  customerId,
  customerName = '',
  accountNumber = '',
  onSuccess,
  onStageConfirmationRequired,
}: Readonly<CaseSendEmailModalProps>) {
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const { data: linkedTransactionIds = [] } = useCaseTransactionIds(open ? caseId : undefined);
  const logCaseEmail = useLogCaseEmail();
  const { isDimensions } = useCompanyProductType();
  const { downloadAdmDocumentAsBase64 } = useAdmDocumentDownloadAsBase64();
  const { getDocumentContext } = useDocumentApi();
  const { getAccessToken } = useAuth();
  const { isDemo } = useMode();
  const { apiGet } = useApi();

  const handleFetchAttachments = useCallback(
    async (onProgress: (done: number, total: number) => void): Promise<FetchAttachmentsResult> => {
      if (linkedTransactionIds.length === 0) return emptyAttachmentsResult();

      if (isDemo) return emptyAttachmentsResult();

      if (isDimensions) {
        // Dimensions path: resolve externalIds then fetch ADM documents
        const ctx = await getDocumentContext();
        if (!ctx) {
          return attachmentsError(attachmentErrorMessages.noDocumentService);
        }

        if (!getAccessToken) {
          return attachmentsError(attachmentErrorMessages.noAuthentication);
        }

        // Fetch transaction details to get externalIds
        const txDetails = await Promise.allSettled(
          linkedTransactionIds.map((txId) => apiGet<TransactionApi>(`/api/v1/transactions/${txId}`)),
        );

        const failedTxCount = txDetails.filter((r) => r.status === 'rejected').length;

        const transactionsWithExternalId = txDetails
          .filter((r): r is PromiseFulfilledResult<TransactionApi> => r.status === 'fulfilled' && !!r.value.externalId)
          .map((r) => r.value);

        if (transactionsWithExternalId.length === 0) {
          return failedTxCount > 0
            ? attachmentsError(attachmentErrorMessages.documentLookupFailed(failedTxCount))
            : emptyAttachmentsResult();
        }

        if (failedTxCount > 0) {
          toast.warning(`${failedTxCount} transaction(s) could not be retrieved for document lookup`);
        }

        // For each transaction, fetch up to 10 ADM documents
        const allDocs: { id: string; fileName: string; fileType: string }[] = [];
        let admLookupFailures = 0;
        for (const tx of transactionsWithExternalId) {
          try {
            const docs = await fetchAdmDocumentsForTransaction(ctx, getAccessToken, tx.externalId!, 10);
            for (const doc of docs) {
              allDocs.push({ id: doc.id, fileName: doc.fileName, fileType: doc.fileType });
            }
          } catch {
            admLookupFailures++;
          }
        }

        if (allDocs.length === 0) {
          return admLookupFailures > 0
            ? attachmentsError(attachmentErrorMessages.documentLookupFailed(admLookupFailures))
            : emptyAttachmentsResult();
        }

        if (admLookupFailures > 0) {
          toast.warning(`${admLookupFailures} transaction(s) failed document lookup`);
        }

        return fetchAttachmentsWithProgress(
          allDocs,
          (doc) => downloadAdmDocumentAsBase64(doc.id, doc.fileName, doc.fileType),
          onProgress,
        );
      }

      // Financials path: keep existing behavior
      return fetchAttachmentsWithProgress(linkedTransactionIds, downloadDocumentAsBase64, onProgress);
    },
    [
      linkedTransactionIds,
      downloadDocumentAsBase64,
      isDimensions,
      isDemo,
      getDocumentContext,
      getAccessToken,
      apiGet,
      downloadAdmDocumentAsBase64,
    ],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
      attachedTransactionIds?: number[];
      paymentLinkBelowMinimum?: PaymentLinkBelowMinimum | null;
    }) => {
      logCaseEmail.mutate(
        {
          sourceId: caseId,
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
          attachedTransactionIds: payload.attachedTransactionIds,
          paymentLinkOmitted: payload.paymentLinkBelowMinimum != null,
        },
        {
          onSuccess: (result) => {
            if (result?.stageConfirmation) {
              onStageConfirmationRequired?.(result.stageConfirmation);
            }
          },
          onError: (error) => {
            trackError(error instanceof Error ? error : new Error('Failed to save case email to history'));
            toast.error('Email was sent but failed to save to history. Please contact support if the issue persists.');
          },
        },
      );
    },
    [logCaseEmail, caseId, onStageConfirmationRequired],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerId}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="collection"
      sourceId={Number(caseId)}
      callbackEventName="creditcontrol:case-email-done"
      attachLabel="Attach outstanding invoices"
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={['collections', 'general']}
      allowedTemplateTypes={['email']}
    />
  );
}
